import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Parses the 'places' URL parameter into an array of place IDs.
 * Treats all comma-separated values as strings (place IDs).
 * @param value The raw string from URLSearchParams.get('places').
 * @returns An array of trimmed, non-empty place IDs.
 */
const parsePlacesParam = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .replace(/^\[|\]$/g, "") // Remove brackets
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean); // Remove empty strings
};

/**
 * Hook to parse URL parameters for place highlighting and terminal selection
 * Supports formats like:
 * - ?places=A-1-2-3,A-1-2-4 (place IDs)
 * - ?places=[A-1-2-3,A-1-2-4] (place IDs with brackets)
 * - ?place=A-1-2-3 (single place ID)
 * - ?terminal_id=5 (terminal selection by ID)
 */
export const useParseUrlHighlightData = () => {
  const location = useLocation(); // This hook provides the current URL location

  const { urlSelectedPlaceIds, urlSelectedTerminalId } = useMemo(() => {
    const params = new URLSearchParams(location.search);

    // Parse 'places' parameter (multiple places)
    let placeIds = parsePlacesParam(params.get("places"));

    // Parse 'place' parameter (single place) - fallback if 'places' is empty
    if (placeIds.length === 0) {
      const placeParam = params.get("place");
      if (placeParam?.trim()) {
        placeIds = [placeParam.trim()];
      }
    }

    // Parse terminal_id parameter
    const terminalIdParam = params.get("terminal_id");
    const terminalId =
      terminalIdParam && !isNaN(parseInt(terminalIdParam, 10))
        ? parseInt(terminalIdParam, 10)
        : null;

    return {
      urlSelectedPlaceIds: placeIds,
      urlSelectedTerminalId: terminalId,
    };
  }, [location.search]);

  return { urlSelectedTerminalId, urlSelectedPlaceIds };
};
